import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import toast from 'react-hot-toast';

import { useAuthStore } from '@/store/auth.store';

const baseURL = import.meta.env.VITE_API_URL?.trim() || '';
const PREFIX = '/api/manager';

export const api = axios.create({ baseURL, timeout: 20_000 });

api.interceptors.request.use((cfg) => {
  const t = useAuthStore.getState().accessToken;
  if (t) cfg.headers.set('Authorization', `Bearer ${t}`);
  return cfg;
});

/**
 * Обновление сессии. Access живёт 15 минут, refresh — 30 дней, поэтому без
 * этого кабинет «слетал» каждые четверть часа. Один общий promise на все
 * параллельные 401: иначе десять запросов инициируют десять ротаций подряд,
 * и последняя перезапишет токены, полученные первыми.
 */
let refreshing: Promise<string | null> | null = null;

async function refreshSession(): Promise<string | null> {
  const { refreshToken, setTokens } = useAuthStore.getState();
  if (!refreshToken) return null;
  try {
    const { data } = await axios.post<{ access_token: string; refresh_token: string }>(
      `${baseURL}${PREFIX}/auth/refresh`,
      { refresh_token: refreshToken },
      { timeout: 20_000 },
    );
    setTokens(data.access_token, data.refresh_token);
    return data.access_token;
  } catch {
    return null;
  }
}

function ensureRefresh(): Promise<string | null> {
  refreshing ??= refreshSession().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

/** Для сокета: access истёк — обновить сессию тем же общим promise, что и у HTTP. */
export function refreshAccessToken(): Promise<string | null> {
  return ensureRefresh();
}

function dropSession() {
  useAuthStore.getState().logout();
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    window.location.replace('/login');
  }
}

type RetriableConfig = InternalAxiosRequestConfig & {
  _retried?: boolean;
  /** Страница показывает ошибку сама — тост был бы вторым таким же сообщением. */
  silent?: boolean;
};

api.interceptors.response.use(
  (r) => r,
  async (err: AxiosError<{ error?: string }>) => {
    const status = err.response?.status;
    const cfg = err.config as RetriableConfig | undefined;

    if (status === 401 && cfg && !cfg._retried) {
      cfg._retried = true;
      const token = await ensureRefresh();
      if (token) {
        cfg.headers.set('Authorization', `Bearer ${token}`);
        return api.request(cfg);
      }
      dropSession();
      return Promise.reject(err);
    }

    if (status === 401) {
      dropSession();
      return Promise.reject(err);
    }

    if (!cfg?.silent) toast.error(err.response?.data?.error ?? err.message);
    return Promise.reject(err);
  },
);

export async function apiGet<T>(path: string, opts?: { silent?: boolean }): Promise<T> {
  const { data } = await api.get<T>(`${PREFIX}${path}`, { silent: opts?.silent } as never);
  return data;
}

export async function apiPost<T>(
  path: string,
  body?: unknown,
  opts?: { silent?: boolean; idempotencyKey?: string },
): Promise<T> {
  const { data } = await api.post<T>(`${PREFIX}${path}`, body, {
    silent: opts?.silent,
    headers: opts?.idempotencyKey ? { 'Idempotency-Key': opts.idempotencyKey } : undefined,
  } as never);
  return data;
}

export async function apiPatch<T>(path: string, body: unknown, opts?: { silent?: boolean }): Promise<T> {
  const { data } = await api.patch<T>(`${PREFIX}${path}`, body, { silent: opts?.silent } as never);
  return data;
}

export async function apiDelete<T>(path: string, opts?: { silent?: boolean; body?: unknown }): Promise<T> {
  const { data } = await api.delete<T>(`${PREFIX}${path}`, { silent: opts?.silent, data: opts?.body } as never);
  return data;
}

/** Файл с полями формы; прогресс — доля отправленного (0–1). */
export async function apiUpload<T>(
  path: string,
  form: FormData,
  opts?: { silent?: boolean; onProgress?: (ratio: number) => void },
): Promise<T> {
  const { data } = await api.post<T>(`${PREFIX}${path}`, form, {
    silent: opts?.silent,
    // Голосовое или фото по мобильному интернету — дольше обычного запроса.
    timeout: 120_000,
    onUploadProgress: (e: { loaded: number; total?: number }) => {
      if (e.total) opts?.onProgress?.(Math.min(1, e.loaded / e.total));
    },
  } as never);
  return data;
}

/** Текст ошибки бэкенда для показа рядом с формой (вместо тоста). */
export function apiErrorMessage(err: unknown, fallback = 'Не удалось выполнить действие'): string {
  const e = err as AxiosError<{ error?: string }>;
  // 413 отдаёт nginx страницей, без нашего JSON.
  if (e?.response?.status === 413) return 'Файл слишком большой';
  return e?.response?.data?.error ?? (e?.message || fallback);
}

/**
 * Ключ идемпотентности для денежных действий. crypto.randomUUID есть только в
 * secure context, а кабинет открывают и по LAN-адресу без https.
 */
export function newIdempotencyKey(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}
